import { Button, Page } from '@dixa/lib-shared-habla';
import { useState } from 'react';

import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { CustomForm } from '../../customForms.types';
import { useCustomForms } from '../../hooks/useCustomForms/useCustomForms';

import {
  AddFormModal,
  DeleteFormModal,
  RenameFormModal,
} from './components/modals';
import { CustomFormsTable } from './components/table/customFormsTable';

export function CustomFormsListPage(): JSX.Element {
  const customForms = useCustomForms();
  const navigate = useAppNavigate();
  const notify = useNotifications();
  const dispatch = useAppDispatch();

  const [renameForm, setRenameForm] = useState<CustomForm | undefined>();
  const [isAddFormModalOpen, setIsAddFormModalOpen] = useState(false);

  const handleEditForm = (form: CustomForm) => {
    navigate({
      path: '/settings/custom-forms/edit/:formId',
      params: { path: { formId: form.id } },
    });
  };

  const duplicateForm = async (form: CustomForm): Promise<void> => {
    const { isConfirmed, confirm } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: 'Are you sure?',
            description: `Are you sure you want to duplicate the form "${form.title}"?`,
            confirm: {
              label: 'Yes, duplicate',
              async: true,
              asyncLabel: 'Duplication in progress...',
              variant: 'primary',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      confirm(async () => {
        const result = await customForms.duplicateForm(form);
        if (result.ok) {
          notify({
            title: 'Form duplicated successfully',
            time: 5000,
            icon: 'done',
            className: 'success',
          });
        } else {
          notify({
            title: 'Could not duplicate the form',
            text: result.error,
            icon: 'exclamation',
            className: 'danger',
          });
        }
      });
    }
  };

  const handleDeleteForm = (form: CustomForm) =>
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:deleteForm',
          arg: { form },
        },
      }),
    );

  return (
    <Page>
      <Page.Head title="Forms" />

      <Page.Body>
        <Page.Section
          title="All forms"
          controls={
            <Button onClick={() => setIsAddFormModalOpen(true)}>
              Add new form
            </Button>
          }
        >
          <CustomFormsTable
            forms={customForms?.forms ?? []}
            isLoading={customForms?.isLoading}
            onEdit={handleEditForm}
            onRename={setRenameForm}
            onDuplicate={duplicateForm}
            onDelete={handleDeleteForm}
          />
        </Page.Section>
      </Page.Body>

      <AddFormModal
        isOpen={isAddFormModalOpen}
        onClose={() => setIsAddFormModalOpen(false)}
      />

      {renameForm && (
        <RenameFormModal
          form={renameForm}
          onClose={() => setRenameForm(undefined)}
        />
      )}

      <DeleteFormModal />
    </Page>
  );
}
